//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { useGetYouTubeUserQuery } from '@account-settings/accountSettingsQueries';
import { useGetYouTubeConnectionStatusQuery } from '@account-settings/components/social-media-accounts/youtube/youtubeAccountSettingsQueries';
import {
  SocialNetworkUserInfo,
  SocialNetworksUserInfo,
} from '@account-settings/redux/accountSettings/accountSettingsModel';
import {
  getConnectedFacebookPageToken,
  getFacebookUserToken,
} from '@apis/facebook-settings/gatewayFacebookSettingsApi';
import { getPageMetadata } from '@apis/gatewayFacebookApi';
import { getConnectedInstagramAccount } from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import { getDefaultInstagramUser } from '@apis/instagram-user/instagramUserApi';
import { getConnectionStatus as getPinterestConnectionStatus } from '@apis/pinterest-oauth/pinterestOAuthApi';
import { getUser as getPinterestUser } from '@apis/pinterest-user/pinterestUserApi';
import { getConnectionStatus as getTikTokConnectionStatus } from '@apis/tiktok-settings/tiktokSettingsApi';
import { getTikTokUser } from '@apis/tiktok-user/tiktokUserApi';
import { getTwitterSettings } from '@apis/twitter-settings/twitterSettingsApi';
import { getUser as getTwitterUser } from '@apis/twitter-user/twitterUserApi';
import { SocialNetwork } from '@models/SocialNetwork';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { SchedulerSocialNetwork } from '@universal-scheduling/models/SocialNetworkModels';
import { allScheduleNetworks } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { isDefined } from '@utils/filterPredicates';
import { getMedsImageUrl } from '@utils/meds';

const SUPPORTED_NETWORKS: SchedulerSocialNetwork[] = [
  SocialNetwork.Facebook,
  SocialNetwork.Instagram,
  SocialNetwork.Pinterest,
  SocialNetwork.TikTok,
  SocialNetwork.Twitter,
  SocialNetwork.YouTube,
];

/**
 * Function to get user info for Facebook
 */
export const useFacebookUserInfoQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.AccountSettingsSocialFacebook,
    ],
    queryFn: async () => {
      const [facebookPageTokenResponse, facebookUserTokenResponse] =
        await Promise.all([
          getConnectedFacebookPageToken(),
          getFacebookUserToken(),
        ]);

      const facebookPageToken =
        facebookPageTokenResponse.data.facebookPageToken;
      const facebookUserToken =
        facebookUserTokenResponse.data.facebookUserToken;

      if (!facebookPageToken?.entityId) {
        return {
          userInfo: null,
          pageToken: null,
          pageMetadata: null,
          isConnected: false,
          isStale: false,
        };
      }

      try {
        const pageMetadata = await getPageMetadata(facebookPageToken.entityId);

        return {
          userInfo: facebookUserToken,
          pageToken: facebookPageToken,
          pageMetadata,
          isConnected: true,
          isStale: false,
        };
      } catch {
        return {
          userInfo: facebookUserToken,
          pageToken: facebookPageToken,
          pageMetadata: null,
          isConnected: true,
          isStale: false,
        };
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Function to get user info for Instagram
 */
export const useInstagramUserInfoQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
    ],
    queryFn: async () => {
      const [connectedAccountResponse, graphApiDefaultAccount] =
        await Promise.all([
          getConnectedInstagramAccount(),
          getDefaultInstagramUser(),
        ]);

      const connectedAccount = connectedAccountResponse.data.connectedAccount;

      if (connectedAccount) {
        const defaultAccount = {
          ...graphApiDefaultAccount.user,
          userId: connectedAccount.userId,
          accountId: connectedAccount.userId,
        };

        return {
          connectedAccount,
          defaultInstagramAccount: defaultAccount,
        };
      }

      return {
        connectedAccount,
        defaultInstagramAccount: null,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Function to get user info for Pinterest
 */
export const usePinterestUserInfoQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.AccountSettingsSocialPinterestUser,
    ],
    queryFn: async () => {
      const [userResponse, settingsResponse] = await Promise.all([
        getPinterestUser(),
        getPinterestConnectionStatus(),
      ]);

      return {
        user: userResponse,
        settings: settingsResponse,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Function to get user info for TikTok
 */
export const useTikTokUserInfoQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.AccountSettingsSocialTikTokUser,
    ],
    queryFn: async () => {
      const [userResponse, settingsResponse] = await Promise.all([
        getTikTokUser(),
        getTikTokConnectionStatus(),
      ]);

      return {
        user: userResponse,
        settings: settingsResponse,
      };
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Function to get user info for Twitter
 */
export const useTwitterUserInfoQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.AccountSettingsSocialTwitter,
    ],
    queryFn: async () => {
      const settingsResponse = await getTwitterSettings();

      const username = settingsResponse.account?.username;

      if (!username) {
        return { settings: settingsResponse, user: undefined };
      }

      try {
        const userResponse = await getTwitterUser({
          twitterUsername: username,
        });

        return {
          settings: settingsResponse,
          user: userResponse,
        };
      } catch {
        return { settings: settingsResponse, user: undefined };
      }
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

/**
 * Combined social networks user info hook
 */
export const useSocialNetworksUserInfoQuery = (
  networks: SocialNetwork[] = allScheduleNetworks,
): SocialNetworksUserInfo => {
  const {
    data: youtubeConnectionInfo,
    isFetched: isFetchedYouTubeConnection,
    isFetching: isFetchingYouTubeConnection,
  } = useGetYouTubeConnectionStatusQuery();
  const {
    data: youtubeUserInfo,
    isFetched: isFetchedYouTubeUser,
    isFetching: isFetchingYouTubeUser,
  } = useGetYouTubeUserQuery();

  const {
    data: facebookData,
    isFetched: isFetchedFacebook,
    isFetching: isFetchingFacebook,
  } = useFacebookUserInfoQuery();
  const {
    data: instagramData,
    isFetched: isFetchedInstagram,
    isFetching: isFetchingInstagram,
  } = useInstagramUserInfoQuery();
  const {
    data: pinterestData,
    isFetched: isFetchedPinterest,
    isFetching: isFetchingPinterest,
  } = usePinterestUserInfoQuery();
  const {
    data: tiktokData,
    isFetched: isFetchedTikTok,
    isFetching: isFetchingTikTok,
  } = useTikTokUserInfoQuery();
  const {
    data: twitterData,
    isFetched: isFetchedTwitter,
    isFetching: isFetchingTwitter,
  } = useTwitterUserInfoQuery();

  const supportedNetworks = networks.filter((network) =>
    SUPPORTED_NETWORKS.includes(network as SchedulerSocialNetwork),
  );

  const networkChecks = supportedNetworks.map((network) => {
    switch (network) {
      case SocialNetwork.Facebook:
        return isFetchedFacebook;
      case SocialNetwork.Instagram:
        return isFetchedInstagram;
      case SocialNetwork.Pinterest:
        return isFetchedPinterest;
      case SocialNetwork.TikTok:
        return isFetchedTikTok;
      case SocialNetwork.Twitter:
        return isFetchedTwitter;
      case SocialNetwork.YouTube:
        return isFetchedYouTubeConnection && isFetchedYouTubeUser;
      default:
        return false;
    }
  });
  const areAllFetched = networkChecks.every(Boolean);

  const socialNetworksUserInfo: SocialNetworkUserInfo[] = networks.map(
    (network) => {
      const baseInfo = {
        network,
        isConnected: false,
        isFetched: false,
        isFetching: false,
        isStale: false,
        bio: undefined,
        followerCount: undefined,
        fullName: undefined,
        profileImageUrl: undefined,
        username: undefined,
        userId: undefined,
      };

      switch (network) {
        case SocialNetwork.Facebook: {
          return {
            ...baseInfo,
            bio: facebookData?.pageMetadata?.biography,
            username: facebookData?.pageMetadata?.username,
            profileImageUrl: facebookData?.pageMetadata?.profilePictureUrl,
            isConnected: !!facebookData?.isConnected,
            isFetched: isFetchedFacebook,
            isFetching: isFetchingFacebook,
            isStale: facebookData?.isStale ?? false,
            userId: facebookData?.pageToken?.entityId.toString(),
          };
        }
        case SocialNetwork.Instagram: {
          const instagramUser = instagramData?.connectedAccount;
          const defaultAccount = instagramData?.defaultInstagramAccount;

          return {
            ...baseInfo,
            bio: defaultAccount?.bio,
            followerCount: defaultAccount?.followerCount,
            fullName: defaultAccount?.fullName,
            username: instagramUser?.username ?? defaultAccount?.username,
            profileImageUrl: defaultAccount?.profileImageUrl,
            isConnected: !!instagramUser,
            isFetched: isFetchedInstagram,
            isFetching: isFetchingInstagram,
            userId: instagramUser?.businessAccountId.toString(),
          };
        }
        case SocialNetwork.Pinterest: {
          return {
            ...baseInfo,
            bio: pinterestData?.user?.about,
            followerCount: pinterestData?.user?.followerCount,
            fullName: pinterestData?.user?.fullName,
            username: pinterestData?.user?.username,
            profileImageUrl: pinterestData?.user?.profileImageUrl,
            isConnected: pinterestData?.settings.isConnected ?? false,
            isFetched: isFetchedPinterest,
            isFetching: isFetchingPinterest,
            isStale: pinterestData?.settings.hasStaleCredentials ?? false,
            userId: pinterestData?.user?.id,
          };
        }
        case SocialNetwork.TikTok: {
          return {
            ...baseInfo,
            followerCount: tiktokData?.user.followersCount,
            fullName: tiktokData?.user.displayName,
            username: tiktokData?.user.username ?? tiktokData?.user.displayName,
            profileImageUrl: tiktokData?.user.profileImage,
            isConnected: tiktokData?.settings.isConnected ?? false,
            isFetched: isFetchedTikTok,
            isFetching: isFetchingTikTok,
            isStale: tiktokData?.settings.hasStaleCredentials ?? false,
            userId: tiktokData?.user.id,
          };
        }
        case SocialNetwork.Twitter: {
          return {
            ...baseInfo,
            bio: twitterData?.user?.description,
            followerCount:
              twitterData?.user?.userMetrics?.publicMetrics?.followersCount,
            fullName: twitterData?.user?.name,
            username: twitterData?.user?.screenName,
            profileImageUrl: twitterData?.user?.profileImageUrl,
            isConnected: !!twitterData?.settings.account,
            isFetched: isFetchedTwitter,
            isFetching: isFetchingTwitter,
            isStale: false,
            userId: twitterData?.user?.id,
          };
        }
        case SocialNetwork.YouTube: {
          return {
            ...baseInfo,
            bio: youtubeUserInfo?.description,
            followerCount: youtubeUserInfo?.subscriberCount,
            username: youtubeUserInfo?.handle,
            profileImageUrl: isDefined(youtubeUserInfo?.thumbnailFileId)
              ? getMedsImageUrl(youtubeUserInfo.thumbnailFileId)
              : youtubeUserInfo?.thumbnailUrl,
            isConnected: !!youtubeConnectionInfo?.isConnected,
            isFetched: isFetchedYouTubeConnection && isFetchedYouTubeUser,
            isFetching: isFetchingYouTubeConnection || isFetchingYouTubeUser,
            isStale: !!youtubeConnectionInfo?.hasStaleCredentials,
            userId: youtubeConnectionInfo?.userId,
          };
        }
        default:
          return baseInfo;
      }
    },
  );

  return {
    areAllFetched,
    socialNetworksUserInfo,
  };
};

/**
 * Hook for getting a single social network's user info
 */
export const useSocialNetworkUserInfoQuery = (
  network: SocialNetwork,
): SocialNetworkUserInfo => {
  const { socialNetworksUserInfo } = useSocialNetworksUserInfoQuery([network]);

  return (
    socialNetworksUserInfo[0] ?? {
      bio: undefined,
      followerCount: undefined,
      fullName: undefined,
      isConnected: false,
      isFetched: true,
      isFetching: false,
      isStale: false,
      network,
      profileImageUrl: undefined,
      username: '',
      userId: undefined,
    }
  );
};
